// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui'
import { Button } from '@/components/ui'
import { HelpCircle } from 'lucide-react'

interface SavingsEstimateProps {
  totalPages: number
}

// Industry benchmarks: minutes per page for manual contract review
const RATE_PRESETS: Record<string, { label: string; minutesPerPage: number }> = {
  fast: { label: 'Fast (2 min/page)', minutesPerPage: 2 },
  standard: { label: 'Standard (5 min/page)', minutesPerPage: 5 },
  thorough: { label: 'Thorough (10 min/page)', minutesPerPage: 10 },
  legal: { label: 'Legal Review (15 min/page)', minutesPerPage: 15 },
}

export const DEFAULT_HOURLY_RATE = 60

export const SAVINGS_CONFIG_STORAGE_KEY = 'metrics-savings-config'

export interface SavingsConfig {
  ratePreset: string
  hourlyRate: number
}

export const loadSavingsConfig = (): SavingsConfig => {
  const config: SavingsConfig = {
    ratePreset: 'standard',
    hourlyRate: DEFAULT_HOURLY_RATE,
  }

  try {
    const stored = localStorage.getItem(SAVINGS_CONFIG_STORAGE_KEY)
    if (!stored) return config

    const saved = JSON.parse(stored)
    if (saved.ratePreset) config.ratePreset = saved.ratePreset
    // cm-coding: Browsers saved the former $35 default as if the user selected it.
    // Retire that value with the new fully loaded labor estimate while preserving
    // every other rate that a user deliberately selected.
    if (saved.hourlyRate && saved.hourlyRate !== 35) config.hourlyRate = saved.hourlyRate
  } catch {
    // Invalid browser settings should not prevent the metrics dashboard from loading.
  }

  return config
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

const formatHours = (minutes: number) => {
  const hours = minutes / 60
  if (hours < 1) return `${minutes} min`
  return `${hours.toFixed(0)} hr`
}

export function SavingsEstimate({ totalPages }: SavingsEstimateProps) {
  const [ratePreset, setRatePreset] = useState('standard')
  const [hourlyRate, setHourlyRate] = useState(DEFAULT_HOURLY_RATE)

  // Load from localStorage on mount
  useEffect(() => {
    const config = loadSavingsConfig()
    setRatePreset(config.ratePreset)
    setHourlyRate(config.hourlyRate)
  }, [])

  // Save to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        SAVINGS_CONFIG_STORAGE_KEY,
        JSON.stringify({ ratePreset, hourlyRate })
      )
    } catch {
      // Ignore storage errors
    }
  }, [ratePreset, hourlyRate])

  const minutesPerPage = RATE_PRESETS[ratePreset]?.minutesPerPage || 5
  const totalMinutes = totalPages * minutesPerPage
  const totalHours = totalMinutes / 60
  const estimatedSavings = totalHours * hourlyRate

  return (
    <div>
      <div className="mt-1.5 text-[27px] font-[650] tracking-[-0.02em] tabular-nums text-[#16a34a]">
        {formatCurrency(estimatedSavings)}
      </div>
      <div className="mt-[3px] text-xs text-slate-400">
        {formatHours(totalMinutes)} saved @ ${hourlyRate}/hr
      </div>

      <Dialog>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 h-6 px-0 text-xs font-[550] text-slate-500 hover:bg-transparent hover:text-slate-700"
          >
            <HelpCircle className="h-3 w-3 mr-1" />
            Adjust
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Savings Estimate Settings</DialogTitle>
            <DialogDescription>
              Customize the calculation based on your organization's review process.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Review Speed</label>
              <Select value={ratePreset} onValueChange={setRatePreset}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RATE_PRESETS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Average time to manually review one page of a contract.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Hourly Rate ($)</label>
              <Select
                value={String(hourlyRate)}
                onValueChange={(v) => setHourlyRate(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[40, 50, 60, 75, 100, 125, 150].map((rate) => (
                    <SelectItem key={rate} value={String(rate)}>
                      ${rate}/hour
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Fully loaded cost of a reviewer (salary + benefits + overhead).
              </p>
            </div>

            <div className="rounded-lg bg-muted p-4 space-y-2">
              <h4 className="font-medium">Calculation</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Pages processed:</span>
                  <span className="font-medium">{totalPages.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Minutes per page:</span>
                  <span className="font-medium">{minutesPerPage}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total time saved:</span>
                  <span className="font-medium">{totalHours.toFixed(1)} hours</span>
                </div>
                <div className="flex justify-between border-t pt-1 mt-1">
                  <span>Estimated savings:</span>
                  <span className="font-bold text-green-600">{formatCurrency(estimatedSavings)}</span>
                </div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              <strong>Methodology:</strong> This estimate assumes automated review replaces
              manual page-by-page contract analysis. Actual savings depend on review complexity,
              reviewer experience, and document types. Industry benchmarks suggest 5-15 minutes
              per page for thorough legal review.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
